import React, { useState, useEffect } from 'react';
import { seriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Layers, Plus, BookOpen, Edit2, Trash2, Link2 } from 'lucide-react';

export default function AdminSeries() {
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '' });
  const [viewSeries, setViewSeries] = useState(null); // detail with articles

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await seriesAPI.list();
      setSeriesList(res.data.series || []);
    } catch { toast.error('Failed to load series'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ title: '', description: '' });
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ title: s.title || '', description: s.description || '' });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      if (editId) {
        await seriesAPI.update(editId, form);
        toast.success('Series updated');
      } else {
        await seriesAPI.create(form);
        toast.success('Series created');
      }
      setShowDialog(false);
      load();
    } catch { toast.error('Failed to save series'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this series? Articles will be unlinked.')) return;
    try {
      await seriesAPI.delete(id);
      toast.success('Series deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const loadDetail = async (id) => {
    try {
      const r = await seriesAPI.get(id);
      setViewSeries(r.data);
    } catch { toast.error('Failed to load series detail'); }
  };

  const removeArticleFromSeries = async (seriesId, articleId) => {
    try {
      await seriesAPI.manageArticles(seriesId, { remove: [articleId] });
      toast.success('Article removed from series');
      loadDetail(seriesId);
      load();
    } catch { toast.error('Failed to remove article'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Layers className="w-6 h-6 text-[#B89B5E]" /> Content Series</h1>
          <p className="text-sm text-[#5B616E] mt-1">Group articles into multi-part series for serial storytelling. Assign articles in the article editor (Meta tab → Series).</p>
        </div>
        <Button onClick={openCreate} className="bg-[#1E5A8A] hover:bg-[#1E5A8A]/90">
          <Plus className="w-4 h-4 mr-2" /> New Series
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : seriesList.length === 0 ? (
        <div className="text-center py-12 text-[#5B616E]">
          <Layers className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>No content series created yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {seriesList.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#B89B5E]/10 flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-[#B89B5E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#1A1A1A] truncate">{s.title}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">
                      <BookOpen className="w-3 h-3 mr-1" /> {(s.article_ids || []).length} articles
                    </Badge>
                    {s.is_active ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Active</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600 text-[10px]">Inactive</Badge>
                    )}
                  </div>
                </div>
              </div>
              {s.description && <p className="text-xs text-[#5B616E] line-clamp-2 mb-3">{s.description}</p>}
              <p className="text-[10px] text-[#5B616E] mb-3">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" className="text-xs" onClick={() => { loadDetail(s.id); }}>
                  <Link2 className="w-3 h-3 mr-1" /> View articles
                </Button>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(s)}>
                  <Edit2 className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="text-xs text-red-500 hover:text-red-700" onClick={() => handleDelete(s.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editId ? 'Edit Series' : 'New Content Series'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Series Title *</label>
              <Input className="mt-1" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Investigation: The Climate Crisis" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="A multi-part series exploring..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1E5A8A]">{editId ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Series detail / article list dialog */}
      <Dialog open={!!viewSeries} onOpenChange={() => setViewSeries(null)}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4" /> {viewSeries?.title}</DialogTitle></DialogHeader>
          {viewSeries && (
            <div className="space-y-3">
              <p className="text-sm text-[#5B616E]">
                Articles in this series. Remove them here or reassign in the article editor (Meta tab → Series).
              </p>
              {(viewSeries.articles || []).length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-4">No articles yet. Assign articles by opening each article editor and selecting this series in the Meta tab.</p>
              ) : (
                <div className="space-y-2">
                  {viewSeries.articles.map((a, i) => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-[#FBF7EF] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[#5B616E]">#{i + 1}</span>
                          <p className="text-sm font-medium truncate">{a.title}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[9px]">{a.status}</Badge>
                          {a.slug && <span className="text-[10px] text-[#5B616E]">/{a.slug}</span>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                        onClick={() => removeArticleFromSeries(viewSeries.id, a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
